import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, RefreshCw } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type EmailTemplate,
  type OutboxRow,
  useRecentEmails,
  useSendTestEmail,
} from "@/hooks/use-email";
import { useSession } from "@/lib/auth-client";
import { TemplatesCard } from "./TemplatesCard";

const TEMPLATE_OPTIONS: { value: EmailTemplate; label: string }[] = [
  { value: "test", label: "Test (pipeline check)" },
  { value: "welcome", label: "Welcome" },
  { value: "password-reset", label: "Password reset" },
  { value: "notification", label: "Notification" },
];

const sendTestSchema = z.object({
  to: z.string().email("Enter a valid email address"),
  template: z.enum(["test", "welcome", "password-reset", "notification"]),
});

type SendTestValues = z.infer<typeof sendTestSchema>;

const STATUS_STYLES: Record<OutboxRow["status"], string> = {
  pending: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400",
  queued: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  sending: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  sent: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  delivered: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  bounced: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
  complained: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
  failed: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
};

function StatusPill({ status }: { status: OutboxRow["status"] }) {
  return (
    <Badge variant="outline" className={`border-transparent ${STATUS_STYLES[status]}`}>
      {status}
    </Badge>
  );
}

export function EmailSettingsTab() {
  const { data: session } = useSession();
  const sendTest = useSendTestEmail();
  const recent = useRecentEmails();

  const form = useForm<SendTestValues>({
    resolver: zodResolver(sendTestSchema),
    defaultValues: { to: session?.user?.email ?? "", template: "test" },
  });

  function onSubmit(values: SendTestValues) {
    sendTest.mutate(values);
  }

  return (
    <div className="space-y-6">
      <TemplatesCard />

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-4 w-4 text-primary" />
            Send a test email
          </CardTitle>
          <CardDescription>
            Renders a template from <code>src/lib/email/templates</code> and enqueues it through the
            transactional pipeline (outbox → SQS → SES).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="grid gap-4 sm:grid-cols-[1fr_220px_auto] sm:items-end"
            >
              <FormField
                control={form.control}
                name="to"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recipient</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="you@example.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      Try <code>success@simulator.amazonses.com</code> to exercise the full
                      pipeline.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="template"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TEMPLATE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={sendTest.isPending}>
                {sendTest.isPending ? "Sending..." : "Send"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card className="border-border/50">
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-lg">Recent emails</CardTitle>
            <CardDescription>The 10 most recently queued outbox rows.</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => recent.refetch()}
            disabled={recent.isFetching}
          >
            <RefreshCw className={`h-4 w-4 ${recent.isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {recent.isError ? (
            <ErrorState
              message={recent.error?.message ?? "Failed to load recent emails"}
              onRetry={() => recent.refetch()}
            />
          ) : (
            <DataTable
              headers={[
                { label: "To" },
                { label: "Subject" },
                { label: "Template" },
                { label: "Status" },
                { label: "Attempts", className: "hidden md:table-cell" },
                { label: "Created", className: "hidden md:table-cell" },
              ]}
              loading={recent.isLoading}
              empty={
                <EmptyState
                  icon={Mail}
                  title="No emails yet"
                  description="Send a test above to see rows appear here."
                />
              }
            >
              {recent.data?.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 text-muted-foreground">{row.toAddresses.join(", ")}</td>
                  <td className="px-4 py-3 font-medium">{row.subject}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.templateName ?? "—"}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={row.status} />
                  </td>
                  <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                    {row.attempts}
                  </td>
                  <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </DataTable>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
